import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TokenPayload } from '../types';

function decodeJwt(token: string): TokenPayload | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded as TokenPayload;
  } catch {
    return null;
  }
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  payload: TokenPayload | null;

  setTokens: (access: string, refresh: string) => void;
  clearTokens: () => void;
  updateAccessToken: (access: string) => void;

  // Convenience getters
  isAuthenticated: () => boolean;
  role: () => TokenPayload['role'] | null;
  estateId: () => string | null;
  userId: () => string | null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      payload: null,

      setTokens(access, refresh) {
        const payload = decodeJwt(access);
        set({ accessToken: access, refreshToken: refresh, payload });
      },

      clearTokens() {
        set({ accessToken: null, refreshToken: null, payload: null });
      },

      updateAccessToken(access) {
        const payload = decodeJwt(access);
        set({ accessToken: access, payload });
      },

      isAuthenticated() {
        const { accessToken, payload } = get();
        if (!accessToken || !payload) return false;
        return payload.exp * 1000 > Date.now();
      },

      role() {
        return get().payload?.role ?? null;
      },

      estateId() {
        return get().payload?.estate_id ?? null;
      },

      userId() {
        return get().payload?.sub ?? null;
      },
    }),
    {
      name: 'patrolit-auth',
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        payload: s.payload,
      }),
    },
  ),
);
